import React from "react";
import { MessagesMessage } from "primereact/messages";
import { Toast, ToastMessage } from "primereact/toast";
import { forwardRef, useImperativeHandle, useRef } from "react";
import "../../styles/components/Messages.scss";

export interface IGlobalMessage {
  messageSeverity: MessagesMessage["severity"];
  messageSummary?: string;
  messageDetail: string | React.JSX.Element;
  isSticky?: boolean;
  messageVisisbilityInMilliSecs?: number;
}

interface IIconList {
  success: React.JSX.Element;
  warn: React.JSX.Element;
  info: React.JSX.Element;
  error: React.JSX.Element;
}

export type GlobalMessageHandle = {
  showGlobalMessage: (props: IGlobalMessage) => void;
  showGlobalMessageList: (globalMessageList: IGlobalMessage[]) => void;
  clearGlobalMessage: () => void;
};

const GlobalMessage = forwardRef<GlobalMessageHandle>((_props, ref) => {
  const toast = useRef<Toast>(null);

  const toastIcon = (iconName: string): React.JSX.Element => (
    <div className="flex align-items-center">
      <i className={`pi ${iconName}`} />
    </div>
  );

  const iconList: IIconList = {
    success: toastIcon("pi-check-circle"),
    info: toastIcon("pi-info-circle"),
    warn: toastIcon("pi-exclamation-triangle"),
    error: toastIcon("pi-times-circle"),
  };

  const getToastContent = (
    messageSeverity: MessagesMessage["severity"],
    summary: React.ReactNode,
    detail: React.ReactNode,
  ) => {
    const icon =
      messageSeverity && messageSeverity in iconList
        ? iconList[messageSeverity as keyof IIconList]
        : null;
    return (
      <>
        {icon}
        <div className="p-toast-message-text">
          {summary && <span className="p-toast-summary">{summary}</span>}
          {detail && <div className="p-toast-detail">{detail}</div>}
        </div>
      </>
    );
  };

  const getGlobalMessageWithDefaultValues = (globalMessage: IGlobalMessage) => {
    return {
      messageSeverity: globalMessage.messageSeverity,
      messageSummary: globalMessage.messageSummary,
      messageDetail: globalMessage.messageDetail,
      isSticky:
        globalMessage.isSticky !== undefined ? globalMessage.isSticky : false,
      messageVisisbilityInMilliSecs:
        globalMessage.messageVisisbilityInMilliSecs !== undefined
          ? globalMessage.messageVisisbilityInMilliSecs
          : 5000,
    };
  };

  useImperativeHandle(ref, () => ({
    showGlobalMessage(globalMessage: IGlobalMessage) {
      const message = getGlobalMessageWithDefaultValues(globalMessage);
      toast?.current?.show({
        life: message.messageVisisbilityInMilliSecs,
        sticky: message.isSticky,
        severity: message.messageSeverity,
        closable: true,
        content: getToastContent(
          message.messageSeverity,
          message.messageSummary,
          message.messageDetail,
        ),
      });
    },
    showGlobalMessageList(globalMessageList: IGlobalMessage[]) {
      // NOTE: the staggered "life" value is functional, *not* just for looks. This is as per the example code in
      //   the following primereact doc (click on of the icons in the code box to toggle full code). Before putting
      //   this in, had two elements in array and only one would disappear automatically; it seems that the other
      //   required hovering over it for it to trigger it to
      //   disappear, and even then I think there was still a small delay after (the delay might have been for the
      //   set life value, not sure though). Now, it seems to work great. HOWEVER: it seems that it doesn't work if
      //   life is 1, so I would assume the reason it doesn't work might have to do with the render cycle (this is
      //   also based on a GitHub issue I'd come across while looking into implementing how to get multiple toasts
      //   to show up at once). If that's the case, time and it being rendered don't really have a strict relationship,
      //   so this isn't really a great solution. However, I don't currently know of a better one, so I'm leaving it
      //   as-is. ALso note that I tried a stagger amount of 10 milliseconds, and that one worked sometimes and didn't
      //   others, and 50 (which is what they used in the example on their doc) didn't have any failures - although
      //   note that I really did not do a lot of testing, only did very little.
      //  - https://v9.primereact.org/toast/#multiple

      const toastMessageList: ToastMessage[] = [];

      for (let i = 0; i < globalMessageList.length; i++) {
        const globalMessage = globalMessageList[i];
        if (!globalMessage) continue;
        const message = getGlobalMessageWithDefaultValues(globalMessage);
        toastMessageList.push({
          life: message.messageVisisbilityInMilliSecs + 50 * i,
          sticky: message.isSticky,
          severity: message.messageSeverity,
          closable: true,
          content: getToastContent(
            message.messageSeverity,
            message.messageSummary,
            message.messageDetail,
          ),
        });
      }

      toast?.current?.show(toastMessageList);
    },
    clearGlobalMessage() {
      toast?.current?.clear();
    },
  }));

  return (
    <div className="global-message-container">
      <Toast ref={toast} className={`p-toast`} position="top-right" />
    </div>
  );
});

GlobalMessage.displayName = "GlobalMessage";
export default GlobalMessage;
